import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CaretDown, CaretRight } from 'phosphor-react';
import { toast } from 'sonner';
import { centralApi, ITelaCatalogo } from '../../hooks/useCentralPermissoes';
import {
  ACOES_PADRAO,
  IModulo,
  moduloDaTela,
  nomeDaTela,
  ordemDosModulos,
  rotuloDaAcao,
} from './modulos';
import {
  AZUL,
  BarraSalvar,
  Busca,
  Cartao,
  Etiqueta,
  Marcador,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

type Filtro = 'todas' | 'liberadas' | 'sem' | 'alteradas';
const GRADE = 'minmax(0, 1fr) 64px 64px 64px 64px minmax(0, 240px)';

interface Props {
  grupoId: number;
  grupoNome: string;
  catalogo: ITelaCatalogo[];
  /** Admin tem tudo pelo backend: a grade só informa. */
  somenteLeitura?: boolean;
  /** Outra aba pede para abrir a grade já filtrada (n muda a cada pedido). */
  buscaPedida?: { texto: string; n: number };
}

export function GrupoTelas({ grupoId, grupoNome, catalogo, somenteLeitura, buscaPedida }: Props) {
  const queryClient = useQueryClient();
  const [mudancas, setMudancas] = useState<Record<string, boolean>>({});
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (buscaPedida?.n) {
      setFiltro('todas');
      setBusca(buscaPedida.texto);
    }
  }, [buscaPedida]);

  const { data: doGrupo, isLoading } = useQuery({
    queryKey: ['central', 'grupo-telas', grupoId],
    queryFn: () => centralApi.telasDoGrupo(grupoId),
  });

  useEffect(() => setMudancas({}), [grupoId]);

  const base = useMemo(() => {
    const m = new Map<number, Set<string>>();
    (doGrupo ?? []).forEach(t => m.set(t.tela_id, new Set(t.acoes)));
    return m;
  }, [doGrupo]);

  const telasAtivas = useMemo(() => catalogo.filter(t => t.ativo), [catalogo]);
  const temNaBase = (telaId: number, acao: string) => !!base.get(telaId)?.has(acao);
  const valor = (telaId: number, acao: string) => {
    const k = `${telaId}:${acao}`;
    return k in mudancas ? mudancas[k] : temNaBase(telaId, acao);
  };
  const alternar = (telaId: number, acao: string, novo?: boolean) =>
    setMudancas(prev => {
      const k = `${telaId}:${acao}`;
      const atual = k in prev ? prev[k] : temNaBase(telaId, acao);
      const alvo = novo ?? !atual;
      const proximo = { ...prev };
      if (alvo === temNaBase(telaId, acao)) delete proximo[k];
      else proximo[k] = alvo;
      return proximo;
    });

  const telasAlteradas = new Set(Object.keys(mudancas).map(k => Number(k.split(':')[0])));
  const q = busca.trim().toLowerCase();

  const modulos = useMemo(() => {
    const porModulo = new Map<string, { modulo: IModulo; telas: ITelaCatalogo[] }>();
    telasAtivas.forEach(t => {
      const modulo = moduloDaTela(t);
      if (!porModulo.has(modulo.chave)) porModulo.set(modulo.chave, { modulo, telas: [] });
      porModulo.get(modulo.chave)!.telas.push(t);
    });
    return ordemDosModulos
      .filter(k => porModulo.has(k))
      .map(k => {
        const item = porModulo.get(k)!;
        item.telas.sort((a, b) => nomeDaTela(a).localeCompare(nomeDaTela(b)));
        return item;
      });
  }, [telasAtivas]);

  const contagem = useMemo(() => {
    let liberadas = 0;
    telasAtivas.forEach(t => {
      if (valor(t.id, 'view')) liberadas += 1;
    });
    return { total: telasAtivas.length, liberadas };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telasAtivas, base, mudancas]);

  const passa = (t: ITelaCatalogo, modulo: IModulo) => {
    const ver = valor(t.id, 'view');
    const okFiltro =
      filtro === 'todas' ||
      (filtro === 'liberadas' && ver) ||
      (filtro === 'sem' && !ver) ||
      (filtro === 'alteradas' && telasAlteradas.has(t.id));
    const okBusca =
      !q ||
      nomeDaTela(t).toLowerCase().includes(q) ||
      t.codigo.toLowerCase().includes(q) ||
      t.path.toLowerCase().includes(q) ||
      modulo.nome.toLowerCase().includes(q);
    return okFiltro && okBusca;
  };

  const salvar = useMutation({
    mutationFn: () =>
      centralApi.salvarTelasDoGrupo(
        grupoId,
        telasAtivas
          .map(t => ({
            tela_id: t.id,
            acoes: t.acoes.map(a => a.acao).filter(a => valor(t.id, a)),
          }))
          .filter(p => p.acoes.length > 0),
      ),
    onSuccess: async () => {
      const n = Object.keys(mudancas).length;
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      setMudancas({});
      toast.success(`Permissões de ${grupoNome} salvas`, {
        description: `${n} ${n === 1 ? 'alteração' : 'alterações'}`,
      });
    },
    onError: () => toast.error('Não foi possível salvar as permissões de telas'),
  });

  if (isLoading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }

  const nMud = Object.keys(mudancas).length;

  return (
    <Flex direction="column" gap="16px">
      <Cartao p={0} overflow="hidden">
        <Flex
          align="center"
          gap="10px"
          wrap="wrap"
          p="16px 20px"
          borderBottom="1px solid rgba(15,23,42,.07)"
        >
          <Box flex="1 1 240px" maxW="340px">
            <Busca valor={busca} onChange={setBusca} placeholder="Buscar tela ou módulo" />
          </Box>
          <Flex gap="6px" wrap="wrap">
            <Pilula
              ativa={filtro === 'todas'}
              onClick={() => setFiltro('todas')}
              contagem={contagem.total}
            >
              Todas
            </Pilula>
            <Pilula
              ativa={filtro === 'liberadas'}
              onClick={() => setFiltro('liberadas')}
              contagem={contagem.liberadas}
            >
              Liberadas
            </Pilula>
            <Pilula
              ativa={filtro === 'sem'}
              onClick={() => setFiltro('sem')}
              contagem={contagem.total - contagem.liberadas}
            >
              Sem acesso
            </Pilula>
            <Pilula
              ativa={filtro === 'alteradas'}
              onClick={() => setFiltro('alteradas')}
              contagem={telasAlteradas.size}
            >
              Alteradas
            </Pilula>
          </Flex>
        </Flex>

        <Box role="table" aria-label="Telas e ações do grupo" overflowX="auto">
          <Box minW="860px">
            <Box
              role="row"
              display="grid"
              gridTemplateColumns={GRADE}
              gap="8px"
              alignItems="center"
              p="10px 20px"
              bg="rgba(255,255,255,.35)"
              borderBottom="1px solid rgba(15,23,42,.07)"
              fontFamily={MONO}
              fontSize="10px"
              fontWeight={500}
              letterSpacing="1px"
              color={TEXTO_SUAVE}
            >
              <span role="columnheader">TELA</span>
              {ACOES_PADRAO.map(a => (
                <Box as="span" role="columnheader" key={a.acao} textAlign="center">
                  {a.rotulo.toUpperCase()}
                </Box>
              ))}
              <span role="columnheader">OUTRAS AÇÕES</span>
            </Box>

            {modulos.map(({ modulo, telas }) => {
              const visiveis = telas.filter(t => passa(t, modulo));
              if ((filtro !== 'todas' || q) && visiveis.length === 0) return null;
              const aberto = q ? true : !!abertos[modulo.chave];
              const liberadasMod = telas.filter(t => valor(t.id, 'view')).length;
              const todasMod = telas.length > 0 && liberadasMod === telas.length;
              return (
                <Box role="rowgroup" key={modulo.chave}>
                  <Box
                    role="row"
                    display="grid"
                    gridTemplateColumns={GRADE}
                    gap="8px"
                    alignItems="center"
                    p="10px 20px"
                    bg="rgba(248,250,252,.75)"
                    borderBottom="1px solid rgba(15,23,42,.06)"
                  >
                    <Flex
                      as="button"
                      type="button"
                      align="center"
                      gap="10px"
                      textAlign="left"
                      minW={0}
                      aria-expanded={aberto}
                      onClick={() => setAbertos(prev => ({ ...prev, [modulo.chave]: !aberto }))}
                    >
                      {aberto ? (
                        <CaretDown size={16} color={TEXTO_SUAVE} />
                      ) : (
                        <CaretRight size={16} color={TEXTO_SUAVE} />
                      )}
                      <Box w="30px" h="30px" borderRadius="10px" bg={modulo.fundo} flex="none" />
                      <Text fontSize="14px" fontWeight={650} color={TEXTO}>
                        {modulo.nome}
                      </Text>
                      <Text fontSize="12px" color={TEXTO_SUAVE}>
                        {liberadasMod} de {telas.length} telas liberadas
                      </Text>
                    </Flex>
                    {somenteLeitura ? (
                      <span />
                    ) : (
                      <Marcador
                        marcado={todasMod}
                        rotulo={`Liberar todas as telas de ${modulo.nome}`}
                        onChange={() => telas.forEach(t => alternar(t.id, 'view', !todasMod))}
                      />
                    )}
                    <span />
                    <span />
                    <span />
                    <Text fontSize="12px" color={TEXTO_SUAVE}>
                      {somenteLeitura ? '' : 'Marca o “ver” de todas as telas do módulo'}
                    </Text>
                  </Box>

                  {aberto &&
                    visiveis.map(t => {
                      const acoesDaTela = new Set(t.acoes.map(a => a.acao));
                      const extras = t.acoes
                        .map(a => a.acao)
                        .filter(a => !ACOES_PADRAO.some(p => p.acao === a))
                        .sort((a, b) => rotuloDaAcao(a).localeCompare(rotuloDaAcao(b)));
                      return (
                        <Box
                          role="row"
                          key={t.id}
                          display="grid"
                          gridTemplateColumns={GRADE}
                          gap="8px"
                          alignItems="center"
                          p="10px 20px 10px 66px"
                          borderBottom="1px solid rgba(15,23,42,.05)"
                          _hover={{ bg: 'rgba(255,255,255,.55)' }}
                        >
                          <Flex role="cell" direction="column" gap="2px" minW={0}>
                            <Flex align="center" gap="8px" wrap="wrap">
                              <Text fontSize="13.5px" fontWeight={600} color={TEXTO}>
                                {nomeDaTela(t)}
                              </Text>
                              {telasAlteradas.has(t.id) && (
                                <Etiqueta tom="aviso">Alterada</Etiqueta>
                              )}
                            </Flex>
                            <Text
                              fontFamily={MONO}
                              fontSize="11px"
                              color={TEXTO_SUAVE}
                              noOfLines={1}
                            >
                              {t.path}
                            </Text>
                          </Flex>
                          {ACOES_PADRAO.map(a => {
                            if (!acoesDaTela.has(a.acao)) {
                              return (
                                <Text
                                  key={a.acao}
                                  textAlign="center"
                                  color="#94A3B8"
                                  aria-label="Ação não existe nesta tela"
                                >
                                  –
                                </Text>
                              );
                            }
                            if (somenteLeitura) {
                              return (
                                <Text key={a.acao} textAlign="center" color={AZUL} fontWeight={700}>
                                  ✓
                                </Text>
                              );
                            }
                            return (
                              <Marcador
                                key={a.acao}
                                marcado={valor(t.id, a.acao)}
                                alterado={`${t.id}:${a.acao}` in mudancas}
                                rotulo={`${a.rotulo} · ${nomeDaTela(t)}`}
                                onChange={() => alternar(t.id, a.acao)}
                              />
                            );
                          })}
                          <Flex role="cell" wrap="wrap" gap="6px">
                            {extras.map(acao => {
                              const marcado = valor(t.id, acao);
                              const alterado = `${t.id}:${acao}` in mudancas;
                              return (
                                <Box
                                  as="label"
                                  key={acao}
                                  display="inline-flex"
                                  alignItems="center"
                                  gap="6px"
                                  h="28px"
                                  px="10px"
                                  borderRadius="999px"
                                  fontSize="12px"
                                  fontWeight={600}
                                  color="#334155"
                                  cursor={somenteLeitura ? 'default' : 'pointer'}
                                  bg={alterado ? '#FEF3C7' : 'rgba(255,255,255,.75)'}
                                  boxShadow={`inset 0 0 0 1px ${alterado ? '#FDE68A' : '#CBD5E1'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={somenteLeitura ? true : marcado}
                                    disabled={somenteLeitura}
                                    onChange={() => alternar(t.id, acao)}
                                    style={{ width: 15, height: 15, accentColor: AZUL }}
                                  />
                                  {rotuloDaAcao(acao)}
                                </Box>
                              );
                            })}
                          </Flex>
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Cartao>

      {!somenteLeitura && (
        <BarraSalvar
          texto={
            nMud === 0
              ? 'Nenhuma alteração pendente. As mudanças valem para todos os usuários do grupo, menos as exceções de cada usuário.'
              : `${nMud} ${nMud === 1 ? 'permissão alterada' : 'permissões alteradas'} em ${grupoNome}. Só são gravadas ao salvar.`
          }
          semMudanca={nMud === 0}
          salvando={salvar.isPending}
          onDesfazer={() => setMudancas({})}
          onSalvar={() => salvar.mutate()}
        />
      )}
    </Flex>
  );
}
